"use client";

import { ArrowRight, ShieldCheck, MapPin, PackageCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Brukarstwo() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: collageRef,
    isVisible: collageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const reassurancePoints = [{
    icon: MapPin,
    title: "Lokalny ekspert",
    desc: "Działamy na terenie całego województwa śląskiego z pełnym zaangażowaniem."
  }, {
    icon: ShieldCheck,
    title: "Gwarancja jakości",
    desc: "Stosujemy certyfikowane materiały i sprawdzone techniki budowlane."
  }, {
    icon: PackageCheck,
    title: "Kompleksowość",
    desc: "Od projektu, przez wykopy, aż po finalne wykończenie nawierzchni."
  }];
  const frames = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ukladanie_kostki_brukowej.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ukladanie_kostki_brukowej.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ukladanie_kostki_brukowej.png-wvc-sizes",
    alt: "Profesjonalne układanie kostki brukowej na podjeździe",
    rotate: "-rotate-3",
    blob: "rounded-lg",
    position: "md:col-start-1 md:col-span-7 md:row-start-1 md:row-span-2",
    z: "z-30",
    tilt: "md:translate-x-0 md:translate-y-0",
    bg: "bg-slate-200"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ogrodzenie_murowane.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ogrodzenie_murowane.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ogrodzenie_murowane.png-wvc-sizes",
    alt: "Nowoczesne ogrodzenie murowane z elementami metalowymi",
    rotate: "rotate-2",
    blob: "rounded-lg",
    position: "md:col-start-8 md:col-span-5 md:row-start-1 md:row-span-1",
    z: "z-20",
    tilt: "md:-translate-x-6 md:translate-y-4",
    bg: "bg-slate-300"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/niwelacja_terenu.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/niwelacja_terenu.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/niwelacja_terenu.webp-wvc-sizes",
    alt: "Prace ziemne i niwelacja terenu pod budowę",
    rotate: "-rotate-2",
    blob: "rounded-lg",
    position: "md:col-start-8 md:col-span-5 md:row-start-2 md:row-span-1",
    z: "z-10",
    tilt: "md:-translate-x-4 md:-translate-y-6",
    bg: "bg-slate-400"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/taras_z_kostki.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/taras_z_kostki.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/taras_z_kostki.png-wvc-sizes",
    alt: "Estetyczny taras wykonany z kostki brukowej",
    rotate: "rotate-3",
    blob: "rounded-lg",
    position: "md:col-start-2 md:col-span-4 md:row-start-3 md:row-span-1",
    z: "z-20",
    tilt: "md:translate-x-4 md:-translate-y-10",
    bg: "bg-slate-200"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/przygotowanie_dzialki.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/przygotowanie_dzialki.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/przygotowanie_dzialki.png-wvc-sizes",
    alt: "Przygotowanie działki pod budowę domu",
    rotate: "-rotate-2",
    blob: "rounded-lg",
    position: "md:col-start-6 md:col-span-6 md:row-start-3 md:row-span-1",
    z: "z-30",
    tilt: "md:-translate-x-8 md:-translate-y-14",
    bg: "bg-slate-300"
  }];
  const tickerStops = ["01 / BRUKARSTWO", "02 / OGRODZENIA", "03 / ROBOTY ZIEMNE", "01 / BRUKARSTWO", "02 / OGRODZENIA", "03 / ROBOTY ZIEMNE"];
  return <section data-section-id="1361" id="brukarstwo-i-ogrodzenia" className="relative overflow-hidden bg-background py-24 md:py-32 text-foreground">
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 bg-blue-100 opacity-60" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 right-[-6rem] h-80 w-80 bg-slate-100 opacity-50" />

      <div className="relative mx-auto max-w-[80rem] px-5 md:px-8">
        <div className="grid grid-cols-1 gap-y-16 md:grid-cols-12 md:gap-x-6 lg:gap-x-10">
          <div ref={introRef} className={`md:col-span-5 transition-all duration-700 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className={`inline-block bg-slate-800 text-white px-3 py-1.5 rounded-md shadow-md transition-transform duration-700 ${introVisible ? "-rotate-2" : "-rotate-[8deg]"}`}>
              <span className="font-default text-[0.75rem] font-medium uppercase tracking-[0.16em]">
                BudSys · Kompleksowe zagospodarowanie
              </span>
            </div>

            <h2 className="font-default font-bold text-foreground mt-6 text-[clamp(2rem,4.2vw,3.625rem)] leading-[1.04] tracking-[-0.015em]">
              Solidne fundamenty i <em className="italic font-default text-blue-600">estetyczne</em> otoczenie.
            </h2>

            <p className="font-default text-foreground/85 mt-6 text-[clamp(1.0625rem,1.15vw,1.1875rem)] leading-[1.55]">
              BudSys to Twój partner w budowie domu na Śląsku. Specjalizujemy się w robotach ziemnych, układaniu kostki brukowej oraz montażu ogrodzeń, dbając o każdy detal Twojej posesji.
            </p>

            <ul className="mt-8 space-y-5">
              {reassurancePoints.map((point, i) => {
              const Icon = point.icon;
              return <li data-index={i} key={point.title} className="flex items-start gap-4">
                    <span className="relative mt-1.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                      <span className="absolute inset-0 rounded-md border-2 border-blue-600" />
                      <span className="h-2 w-2 rounded-md bg-blue-600" />
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
                        <h3 className="font-default font-bold text-foreground text-[clamp(1rem,1.1vw,1.125rem)] leading-[1.3]">
                          {point.title}
                        </h3>
                      </div>
                      <p className="font-default text-foreground/75 mt-1 text-base leading-[1.6]">
                        {point.desc}
                      </p>
                    </div>
                  </li>;
            })}
            </ul>

            <div className="mt-10">
              <Link to="/OfferPage" className="group inline-flex items-center gap-2 font-default font-medium text-foreground">
                <span className="relative">
                  Zobacz pełną ofertę
                  <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-blue-600 transition-all duration-300 ease-out group-hover:w-full" />
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.75} />
              </Link>
            </div>
          </div>

          <div ref={collageRef} className={`md:col-span-7 transition-all duration-700 ease-out ${collageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative">
              <div className="flex flex-col gap-6 md:grid md:grid-cols-12 md:grid-rows-3 md:gap-0 md:h-[640px] lg:h-[720px]">
                {frames.map((frame, i) => <div data-index={i} key={frame.src} className={`group relative ${frame.position} ${frame.z} ${frame.tilt}`} style={{
                transitionDelay: `${i * 80}ms`
              }}>
                    <div aria-hidden="true" className={`absolute inset-0 ${frame.blob} ${frame.bg} opacity-90 transition-transform duration-500 ease-out md:group-hover:translate-x-2 md:group-hover:-translate-y-2`} />
                    <div className={`relative ${frame.blob} overflow-hidden ${frame.rotate} shadow-md transition-transform duration-500 ease-out h-56 md:h-full`}>
                      <img src={frame.src} data-wvc-srcset={frame.srcset} data-wvc-sizes={frame.sizes} alt={frame.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out md:group-hover:scale-[1.04]" />
                    </div>
                  </div>)}
              </div>

              <div className="relative mt-10 overflow-hidden border-t border-dashed border-slate-300 py-5">
                <div className="flex items-center gap-12 animate-[ticker_40s_linear_infinite] hover:[animation-play-state:paused] whitespace-nowrap">
                  {tickerStops.concat(tickerStops).map((stop, i) => <div data-index={i} key={`${stop}-${i}`} className="flex items-center gap-4 shrink-0">
                      <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                        <span className="absolute inset-0 rounded-md border-2 border-blue-600" />
                        <span className="h-2 w-2 rounded-md bg-blue-600" />
                      </span>
                      <span className="font-default text-xs font-medium uppercase tracking-[0.16em] text-foreground/80">
                        {stop}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-8 py-6 font-default font-bold shadow-md hover:scale-[1.02] transition-transform duration-200">
                <Link to="/PricingPage">Bezpłatna wycena</Link>
              </Button>
              <span className="font-default text-xs uppercase tracking-[0.16em] text-foreground/70">
                Śląsk · Profesjonalne wykonawstwo
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}